import { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import {
  selectDeleteAccountPending,
  selectUser,
} from "../../store/slices/user/slice";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { toast } from "../../components/modern-ui/sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { deleteAccount } from "../../store/slices/user/asyncThunk";

const DeleteAccountPage = () => {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectDeleteAccountPending);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    if (confirmEmail !== user?.username) {
      toast.error("Username do not match. Please try again.");
      return;
    }

    setShowConfirmModal(true);
  };

  console.log(confirmEmail);

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);

    try {
      dispatch(deleteAccount({ username: confirmEmail }))
        .unwrap()
        .then(() => {
          toast.success(
            "Your account is scheduled for deletion in 7 days. Deleting your account is permanent. All credits, videos, and personal data will be erased and cannot be recovered. Later, you can create a brand new account, but you will not receive free credits.",
          );
        })
        .catch((err) => {
          toast.error(
            "An error occurred while deleting your account. Please try again.",
          );
        });
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
    }
  };

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(user?.username);
      toast.success("User ID copied to clipboard!");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <MainContainer>
      <DeleteContainer $theme={theme}>
        <Header>
          <Title>DELETE ACCOUNT</Title>
          <LastUpdated>Account deletion is permanent</LastUpdated>
        </Header>

        <Content>
          <WarningSection $theme={theme}>
            <WarningText $theme={theme}>
              Deleting your account is <strong>permanent</strong>. All credits,
              videos, and personal data will be erased and cannot be recovered.
            </WarningText>
            <SubWarningText $theme={theme}>
              Later, you can create a brand new account, but you will not
              receive free credits.
            </SubWarningText>
          </WarningSection>

          <Section>
            <SectionTitle $theme={theme}>Account Username</SectionTitle>
            <EmailDisplay onClick={handleCopy} $theme={theme}>
              {user?.username}
            </EmailDisplay>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>Confirm Deletion</SectionTitle>
            <Form>
              <InputGroup>
                <Label $theme={theme}>Re-enter your username to confirm</Label>
                <Input
                  type="text"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="Enter your username"
                  required
                  $theme={theme}
                />
              </InputGroup>

              <ButtonGroup>
                <CancelButton
                  onClick={() => {
                    navigate(-1);

                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                  }}
                  $theme={theme}
                >
                  Cancel
                </CancelButton>
                <DeleteButton
                  onClick={handleDeleteAccount}
                  disabled={isLoading || confirmEmail !== user?.username}
                  $theme={theme}
                >
                  {isLoading ? "Deleting Account..." : "Delete Account"}
                </DeleteButton>
              </ButtonGroup>
            </Form>
          </Section>
        </Content>
      </DeleteContainer>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Account"
        message="Are you absolutely sure you want to delete your account? This action cannot be undone."
        confirmText="Delete Account"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={isLoading}
        theme={theme}
      />
    </MainContainer>
  );
};

export default DeleteAccountPage;

const DeleteContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  font-family: Figtree;
  font-weight: 400;
  line-height: 1.6;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-family: Figtree;
  font-weight: 600;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #2877d9, #2877d9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const Content = styled.div``;

const WarningSection = styled.div`
  font-size: 16px;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(40, 119, 217, 0.1);
  border-left: 4px solid #2877d9;
  border-radius: 8px;
`;

const WarningText = styled.div`
  margin-bottom: 15px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};

  strong {
    color: #2877d9;
  }
`;

const SubWarningText = styled.div`
  color: ${({ $theme }) =>
    $theme === "light" ? "rgba(26, 26, 46, 0.7)" : "rgba(255, 255, 255, 0.8)"};
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-family: Figtree;
  font-weight: 600;
  color: #2877d9;
  margin-bottom: 15px;
  border-bottom: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  padding-bottom: 8px;
`;

const EmailDisplay = styled.div`
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(26, 26, 46, 0.05)"
      : "rgba(255, 255, 255, 0.05)"};
  border: ${({ $theme }) =>
    $theme === "light"
      ? "1px solid rgba(26, 26, 46, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.1)"};
  border-radius: 8px;
  padding: 15px;
  font-family: Figtree;
  font-weight: 500;
  color: #2877d9;
  font-size: 16px;
  margin-bottom: 15px;
  cursor: pointer;
`;

const Form = styled.div`
  margin-top: 20px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  font-size: 14px;
  font-family: Figtree;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(26, 26, 46, 0.05)"
      : "rgba(255, 255, 255, 0.05)"};
  border: ${({ $theme }) =>
    $theme === "light"
      ? "1px solid rgba(26, 26, 46, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.1)"};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  font-family: Figtree;
  font-weight: 400;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ $theme }) =>
      $theme === "light"
        ? "rgba(26, 26, 46, 0.5)"
        : "rgba(255, 255, 255, 0.5)"};
  }

  &:focus {
    outline: none;
    border-color: #2877d9;
    box-shadow: 0 0 0 2px rgba(40, 119, 217, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background: transparent;
  border: ${({ $theme }) =>
    $theme === "light"
      ? "1px solid rgba(26, 26, 46, 0.3)"
      : "1px solid rgba(255, 255, 255, 0.3)"};
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-family: Figtree;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $theme }) =>
      $theme === "light"
        ? "rgba(26, 26, 46, 0.05)"
        : "rgba(255, 255, 255, 0.05)"};
    border-color: ${({ $theme }) =>
      $theme === "light"
        ? "rgba(26, 26, 46, 0.5)"
        : "rgba(255, 255, 255, 0.5)"};
  }
`;

const DeleteButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #2877d9, #2877d9);
  border: none;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-family: Figtree;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2877d9, #2877d9);
    transform: translateY(-1px);
    box-shadow: ${({ $theme }) =>
      $theme === "light"
        ? "0 4px 15px rgba(255, 71, 87, 0.2)"
        : "0 4px 15px rgba(255, 71, 87, 0.3)"};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;
